import { Chip, Tooltip } from '@mui/joy';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { scoreSchema } from '../../settings/report-form/schema';

import { Input } from '~/components/ui/input';
import { Text } from '~/components/ui/text';

export function RiskScoreChip({
  value,
  onBlur,
  isEditable,
}: {
  isEditable: boolean;
  value: number | null;
  onBlur: (value: number) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [score, setScore] = useState(value);
  const [errorMessage, setErrorMessage] = useState('');
  const intl = useIntl();
  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    const parseResult = scoreSchema.safeParse(e.target.value);
    if (!parseResult.success) {
      setErrorMessage(parseResult.error.format()._errors.join(', '));
      return;
    }
    setErrorMessage('');
    setScore(parseResult.data);
    onBlur(parseResult.data);
    setIsEditing(false);
  };

  if (!isEditable) {
    return (
      <Chip color='neutral' size='md'>
        <FormattedMessage defaultMessage='Risk Score: ' /> {score ?? 0}
      </Chip>
    );
  }

  return isEditing ? (
    <div className='flex flex-col gap-2 items-center'>
      <Chip color='warning'>
        <div className='flex flex-row items-center gap-2 p-1'>
          <FormattedMessage defaultMessage='Risk Score (0-10): ' />
          <Input
            defaultValue={score ?? 0}
            onBlur={handleBlur}
            size='sm'
            type='number'
            slotProps={{
              input: {
                max: 10,
                min: 0,
              },
            }}
          />
        </div>
      </Chip>
      {errorMessage && <Text className='text-red-500'>{errorMessage}</Text>}
    </div>
  ) : (
    <Tooltip
      title={intl.formatMessage({
        defaultMessage: 'Double click to edit.',
      })}
      placement='top'
    >
      <Chip
        color='warning'
        slotProps={{
          action: {
            onDoubleClick: () => setIsEditing(true),
          },
        }}
        size='md'
      >
        {score === null ? (
          <FormattedMessage defaultMessage='Set Risk Score' />
        ) : (
          <>
            <FormattedMessage defaultMessage='Risk Score: ' /> {score}
          </>
        )}
      </Chip>
    </Tooltip>
  );
}
